import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import ContactForm, { ContactFormData } from './ContactForm';
import { ShortlistProfile } from '../types';

interface ShortlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  shortlistedProfiles: ShortlistProfile[];
  onRemoveFromShortlist: (profileId: string) => void;
  onSubmitQuote: (formData: ContactFormData) => Promise<string> | void;
  loading?: boolean;
}

const ShortlistModal: React.FC<ShortlistModalProps> = ({
  isOpen,
  onClose,
  shortlistedProfiles,
  onRemoveFromShortlist,
  onSubmitQuote,
  loading = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Modal Content */}
      <div
        className="relative flex flex-col mx-auto w-full h-full sm:max-w-6xl sm:mx-auto sm:rounded-lg sm:my-8 sm:max-h-[90vh] sm:h-[90vh]"
        style={{
          backgroundColor: '#FFFFFF',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6" style={{ borderBottom: '1px solid #E5E7EB' }}>
          <div className="flex-1 text-center">
            <h2
              className="text-2xl font-bold"
              style={{
                color: '#03238C',
                fontWeight: '700',
                fontFamily: 'Inter, sans-serif',
                fontSize: '32px'
              }}
            >
              Confirm Your Shortlist
            </h2>
            <p className="mt-1" style={{ color: '#6B7280' }}>
              Review your shortlisted profiles and share your details so we can follow up to confirm availability and next steps.
            </p>
          </div>
          <button
            onClick={onClose}
            className="transition-colors duration-200 p-2 font-bold uppercase"
            style={{ color: '#6B7280' }}
            onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.color = '#03238C'}
            onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.color = '#6B7280'}
          >
            <FontAwesomeIcon icon={faTimes} className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden min-h-0">
          <div className="h-full flex flex-col lg:flex-row min-h-0">
            {/* Left Side - Shortlist Cards */}
            <div className="lg:w-1/2 p-4 sm:p-6 flex flex-col overflow-hidden min-h-0" style={{ borderBottom: '1px solid #E5E7EB', borderRight: '1px solid #E5E7EB' }}>
              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <h3
                  className="text-lg font-semibold"
                  style={{
                    color: '#FF5C3E',
                    fontWeight: '700',
                    fontFamily: 'Inter, sans-serif'
                  }}
                >
                  Your Shortlist ({shortlistedProfiles.length})
                </h3>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-bold uppercase flex items-center justify-center space-x-2 transition-colors duration-200"
                  style={{
                    backgroundColor: '#E9F7FF',
                    color: '#03238C',
                    fontSize: 'var(--dreamdev-font-size-sm)',
                    fontWeight: '700',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e: any) => {
                    e.currentTarget.style.backgroundColor = '#D1EFFF';
                  }}
                  onMouseLeave={(e: any) => {
                    e.currentTarget.style.backgroundColor = '#E9F7FF';
                  }}
                >
                  <span>ADD MORE TALENT</span>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <div className="space-y-4">
                  {shortlistedProfiles.map((profile) => (
                    <div
                      key={profile.id}
                      className="rounded-lg p-4"
                      style={{
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E5E7EB',
                        borderRadius: '6px'
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h4
                            className="font-medium text-lg mb-2"
                            style={{
                              color: '#03238C',
                              fontWeight: '700',
                              fontFamily: 'Inter, sans-serif'
                            }}
                          >
                            {profile.title}
                          </h4>
                          <p className="text-sm mb-3" style={{ color: '#6B7280' }}>{profile.experience}</p>
                          <div className="flex flex-wrap gap-2">
                            {profile.industries && profile.industries.length > 0 ? (
                              profile.industries.map((industry, index) => (
                                <span
                                  key={index}
                                  className="px-3 py-1 text-sm rounded-full"
                                  style={{
                                    backgroundColor: '#CEECFF',
                                    color: '#03238C',
                                    fontWeight: '400',
                                    fontFamily: 'Source Sans 3, sans-serif'
                                  }}
                                >
                                  {industry}
                                </span>
                              ))
                            ) : (
                              profile.skills.map((skill, index) => (
                                <span
                                  key={index}
                                  className="px-3 py-1 text-sm rounded-full"
                                  style={{
                                    backgroundColor: '#CEECFF',
                                    color: '#03238C',
                                    fontWeight: '400',
                                    fontFamily: 'Source Sans 3, sans-serif'
                                  }}
                                >
                                  {skill}
                                </span>
                              ))
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => onRemoveFromShortlist(profile.id)}
                          className="ml-4 transition-colors duration-200 p-1 font-bold uppercase"
                          style={{ color: '#6B7280' }}
                          onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.color = '#FF5C3E'}
                          onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.color = '#6B7280'}
                        >
                          <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Side - Contact Form */}
            <div className="lg:w-1/2 p-4 sm:p-6 flex flex-col overflow-hidden min-h-0">
              <h3
                className="text-lg font-semibold mb-4 flex-shrink-0"
                style={{
                  color: '#03238C',
                  fontWeight: '700',
                  fontFamily: 'Inter, sans-serif'
                }}
              >
                Contact Information
              </h3>
              <div className="flex-1 overflow-y-auto">
                <ContactForm
                  onSubmit={onSubmitQuote}
                  loading={loading}
                  shortlistedProfiles={shortlistedProfiles}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShortlistModal;
